import { Injectable } from '@angular/core';
import {
  ArticleCategoryDTO,
  MaterialCategoryDTO,
  MixtureCategoryDTO,
  OtherArticleIdentifierDto,
  OtherArticleNamesDto,
  ProductNodeDto,
  SubstanceDTO
} from '@scip/api';
import { ProductControllerService } from '@scip/api';
import { intersectionBy } from 'lodash-es';
import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { translationKeys, ProductUtils } from '../core';
import { BackendErrorService } from './backend-error.service';
import { NotificationService } from './notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';

export interface SelectedNode {
  product?: ProductNodeDto;
  substance?: SubstanceDTO;
}

export type ProductTabIdentifier = string; // Its the "id" of the product (coming from the route, therefore a string)
export type ProductNodeIdentifier = string; // its a combination of multiple node attributes to identify a product node
type Category = ArticleCategoryDTO | MaterialCategoryDTO | MixtureCategoryDTO;

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  // We save per tab all loaded nodes (they are loaded lazy on expand) and do not call the backend then again for the same node
  private tabBasedOriginalProductNodeDtos: Map<ProductTabIdentifier, Map<ProductNodeIdentifier, ProductNodeDto>> = new Map();
  // We save per tab all dirty nodes (currently every change from the user) and send then the changes later to the backend
  private tabBasedUserEditedProductNodeDtos: Map<ProductTabIdentifier, Map<ProductNodeIdentifier, ProductNodeDto>> = new Map();
  private changeHappened: Subject<ProductTabIdentifier[]> = new ReplaySubject<ProductTabIdentifier[]>(1);

  constructor(
    private productControllerService: ProductControllerService,
    private backendErrorService: BackendErrorService,
    private notificationService: NotificationService,
    private logger: NGXLogger
  ) {}

  setUserChangedProductNode(tabIdentifier: ProductTabIdentifier, productNode: ProductNodeDto): void {
    const productNodeIdentifier: string = ProductUtils.getProductNodeDtoIdentifier(productNode);
    const originalNode = this.tabBasedOriginalProductNodeDtos.get(tabIdentifier).get(productNodeIdentifier);
    const userNodeIsDirty = this.compareUserEditedAndOriginals(productNode, originalNode);

    // if nodes equal do nothing, remove from dirty (node and if no node anymore, tab id aswell), trigger changeHappened
    // else all else
    if (userNodeIsDirty) {
      // If its the first change for the tab, we create the map on the edited-map
      if (!this.tabBasedUserEditedProductNodeDtos.has(tabIdentifier)) {
        this.tabBasedUserEditedProductNodeDtos.set(tabIdentifier, new Map<ProductNodeIdentifier, ProductNodeDto>());
        this.changeHappened.next();
      }
      // if nodes not equal, do this
      // Save the changed node to be able to later save them or show them again
      this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier).set(ProductUtils.getProductNodeDtoIdentifier(productNode), productNode);
    } else if (this.tabBasedUserEditedProductNodeDtos.has(tabIdentifier)) {
      this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier).delete(productNodeIdentifier);
      if (this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier).size === 0) {
        this.tabBasedUserEditedProductNodeDtos.delete(tabIdentifier);
        this.changeHappened.next();
      }
    }
  }

  discardTabs(tabIds: string[]): void {
    tabIds.forEach((tabId) => {
      this.tabBasedOriginalProductNodeDtos.delete(tabId);
      this.tabBasedUserEditedProductNodeDtos.delete(tabId);
    });
    this.changeHappened.next();
  }

  getChangedTabIds(): ProductTabIdentifier[] {
    return Array.from(this.tabBasedUserEditedProductNodeDtos.keys());
  }

  getUserEditedKeysAsArray(): Observable<ProductTabIdentifier[]> {
    //compare nodes and return array
    return this.changeHappened.asObservable().pipe(
      map(() => {
        return this.getChangedTabIds();
      })
    );
  }

  discardAllTabs(): void {
    // User closed all tabs / navigated away, so lets clear everything in the service
    this.tabBasedOriginalProductNodeDtos.clear();
    this.tabBasedUserEditedProductNodeDtos.clear();
    this.changeHappened.next();
  }

  retrieveProductNodes(id: number, resourceIdentifier: string, orderId: number, isRootNode: boolean = false): Observable<ProductNodeDto[]> {
    return this.productControllerService.getProductDeclarationById(id, resourceIdentifier, orderId).pipe(
      map((response) => {
        if (response === null) {
          this.logger.error('ProductService: The backend call was successfully, but the response from the backend was null for the product with', id);
          throwError(undefined);
        } else {
          this.setProductNode(`${id}`, response);
          return response;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (isRootNode && error.status === 423) {
          // If its the root node which could not be loaded, its high likely the product is not loaded
          // therefore do not show a generic error text, show a dedicated text for that case
          this.backendErrorService.handleError(error, translationKeys.productDeclarationComponent.typescript.errorMessage.productDeclarationInProcessError);
        } else if (error.status === 423) {
          this.backendErrorService.handleError(error);
        } else {
          this.backendErrorService.handleErrorWithInterpolation(
            error,
            translationKeys.productDeclarationComponent.typescript.errorMessage.errorRetrievingProductdeclaration,
            { productIdentifier: id }
          );
        }
        return throwError(error);
      })
    );
  }

  saveTab(tabIdentifier: ProductTabIdentifier): Observable<boolean> {
    const results: ProductNodeDto[] = [];
    if (this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier)?.values()) {
      for (const dirtyNode of this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier).values()) {
        const originalNode = this.tabBasedOriginalProductNodeDtos.get(tabIdentifier).get(ProductUtils.getProductNodeDtoIdentifier(dirtyNode));
        this.compareNodes(dirtyNode, originalNode, results);
      }
    }

    if (results.length > 0) {
      return this.productControllerService.editProductTree(results).pipe(
        map(() => {
          this.notificationService.showSuccess(translationKeys.productService.msg.success.saveSuccess);
          // Call is success, we update our cache (productDetailsMap) so when the user clicks on the node again he gets the latest without the need to hit the backend again.
          this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier).forEach((value, key) => {
            this.tabBasedOriginalProductNodeDtos.get(tabIdentifier).set(key, ProductUtils.copyProducts(value));
          });
          // Dirty cache is clean now as they are saved, change detection should not find any thing to check if the user clicks save.
          this.tabBasedUserEditedProductNodeDtos.delete(tabIdentifier);
          this.changeHappened.next();
          return true;
        }),
        catchError((error) => {
          this.backendErrorService.handleError(error, translationKeys.productService.msg.error.saveError);
          return of(false);
        })
      );
    } else {
      this.notificationService.showWarning(translationKeys.productService.msg.warning.noChangeDetected);
      return of(false);
    }
  }

  getRecentProductNodeForSelector(tabIdentifier: ProductTabIdentifier, wholeIdentifierTogether: string): ProductNodeDto {
    // If we have a edited version return that otherwise the original-productNodeDto
    const productNode =
      this.tabBasedUserEditedProductNodeDtos.get(tabIdentifier)?.get(wholeIdentifierTogether) ||
      this.tabBasedOriginalProductNodeDtos.get(tabIdentifier)?.get(wholeIdentifierTogether);
    // Copy the object to not mutate our cached version
    return ProductUtils.copyProducts(productNode);
  }

  getRecentProductNodeOrSubstanceForSelector(tabIdentifier: ProductTabIdentifier, wholeIdentifierTogether: string): SelectedNode {
    // The caller of the method only has a selector, he does not know if its a substance or a product, but we want to tell
    // it to him, therefore we answer with an object so that he knows what kind of node he gets.
    if (ProductUtils.isSubstanceIdentifier(wholeIdentifierTogether)) {
      // The identifier lead to a substance
      return {
        substance: this.getSubstanceOfNodeForSelector(tabIdentifier, wholeIdentifierTogether)
      };
    } else {
      // The identifier lead to a product
      return {
        product: this.getRecentProductNodeForSelector(tabIdentifier, wholeIdentifierTogether)
      };
    }
  }

  private setProductNode(tabIdentifier: ProductTabIdentifier, data: ProductNodeDto[]) {
    // Lets cache the nodes we retrieve from the backend
    if (data !== undefined) {
      // If its the root node (first loaded node), we need to create the map in our "cache"/"original"-map
      if (!this.tabBasedOriginalProductNodeDtos.has(tabIdentifier)) {
        this.tabBasedOriginalProductNodeDtos.set(tabIdentifier, new Map<ProductNodeIdentifier, ProductNodeDto>());
      }

      for (const node of data) {
        this.tabBasedOriginalProductNodeDtos.get(tabIdentifier).set(ProductUtils.getProductNodeDtoIdentifier(node), ProductUtils.copyProducts(node));
      }
    }
  }

  private getSubstanceOfNodeForSelector(tabIdentifier: ProductTabIdentifier, wholeIdentifierTogether: string): SubstanceDTO {
    // First lets split up the selector into the product- and the substance-identifier
    const productIdentifier = ProductUtils.getProductNodeIdentifierForSubstance(wholeIdentifierTogether);
    const substanceIdentifier = ProductUtils.getOnlySubstanceIdentifier(wholeIdentifierTogether);

    // Then lets get the product for our splitted selector, based on that we get the substance then
    const theProductWithTheSubstance = this.getRecentProductNodeForSelector(tabIdentifier, productIdentifier);

    // The substance is currently only displayed and it is not editable, therefore no need to create a copy of it
    return ProductUtils.getSubstanceOfProductNode(theProductWithTheSubstance, substanceIdentifier);
  }

  private compareNodes(dirty: ProductNodeDto, orig: ProductNodeDto, results: ProductNodeDto[]) {
    const productNodeDtoKeys = Object.keys(dirty);
    const outPut: ProductNodeDto = {};
    for (const key of productNodeDtoKeys) {
      // As a fallback, if on the edited (dirty) object the value is "undefined", we look into the original object
      // what typeof it was before the change to be able to select the correct switch-case
      switch (typeof dirty[key] || typeof orig[key]) {
        //check for typeof String
        case 'string':
        case 'number':
        case 'boolean': {
          if (orig[key] !== dirty[key]) {
            outPut[key] = dirty[key];
          }
          break;
        }
        default: {
          //check for otherArticleIdentifiers
          if (key === 'otherArticleIdentifiers') {
            if (this.compareOtherArtIds(orig[key], dirty[key])) {
              outPut[key] = dirty[key];
              outPut.otherArticleIdentifiersChanged = true;
            }
            //check for any categories
          } else if (key === 'articleCategories' || key === 'materialCategories' || key === 'mixtureCategories') {
            if (this.compareCategories(orig[key], dirty[key])) {
              outPut[key] = dirty[key];
              outPut[key + 'Changed'] = true; // Used in the backend as indicator that the categories changed
            }
          } else if (key === 'otherArticleNames') {
            if (this.compareOtherArticleNames(orig[key], dirty[key])) {
              outPut[key] = dirty[key];
              outPut.otherArticleNamesChanged = true;
            }
          }
        }
      }
    }
    if (Object.keys(outPut).length) {
      outPut.identifier = orig.identifier;
      results.push(outPut);
    }
  }

  private compareUserEditedAndOriginals(dirty: ProductNodeDto, orig: ProductNodeDto): boolean {
    const productNodeDtoKeys = Object.keys(dirty);
    for (const key of productNodeDtoKeys) {
      // As a fallback, if on the edited (dirty) object the value is "undefined", we look into the original object
      // what typeof it was before the change to be able to select the correct switch-case
      switch (typeof dirty[key] || typeof orig[key]) {
        //check for typeof String
        case 'string':
        case 'number':
        case 'boolean': {
          if (orig[key] !== dirty[key]) {
            return true;
          }
          break;
        }
        default: {
          //check for otherArticleIdentifiers
          if (key === 'otherArticleIdentifiers') {
            if (this.compareOtherArtIds(orig[key], dirty[key])) {
              return true;
            }
            //check for any categories
          } else if (key === 'articleCategories' || key === 'materialCategories' || key === 'mixtureCategories') {
            if (this.compareCategories(orig[key], dirty[key])) {
              return true;
            }
          } else if (key === 'otherArticleNames') {
            if (this.compareOtherArticleNames(orig[key], dirty[key])) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  private compareCategories(categoryA: Category[], categoryB: Category[]): boolean {
    if ((!categoryA && !categoryB) || (!categoryA?.length && !categoryB?.length)) {
      return false;
    }
    if (categoryA !== categoryB) {
      const intersection: Category[] = intersectionBy(categoryA, categoryB, 'identifier');
      if (intersection.length !== categoryB?.length || intersection.length !== categoryA?.length) {
        return true;
      }
    }
    return false;
  }

  private compareOtherArtIds(otherArticleIdentifierOriginal: OtherArticleIdentifierDto[], otherArticleIdentifierChanged: OtherArticleIdentifierDto[]): boolean {
    //compare Other Article Identifiers lists
    if (otherArticleIdentifierOriginal) {
      if (otherArticleIdentifierOriginal.length !== otherArticleIdentifierChanged.length) {
        //if length has changed, a change has been detected
        return true;
      } else {
        //if lenght has not changed then check if content has changed
        return otherArticleIdentifierOriginal.some(
          (element) => !otherArticleIdentifierChanged.find((data) => data.value === element.value && data.pickListData.text === element.pickListData.text)
        );
      }
    } else {
      return !!otherArticleIdentifierChanged;
    }
  }

  private compareOtherArticleNames(otherArticleNameOriginal: OtherArticleNamesDto[], otherArticleNameChanged: OtherArticleNamesDto[]): boolean {
    if (otherArticleNameOriginal) {
      if (otherArticleNameOriginal.length !== otherArticleNameChanged.length) {
        //if length has changed, a change has been detected
        return true;
      } else {
        //if lenght has not changed then check if content has changed
        return otherArticleNameOriginal.some(
          (element) =>
            !otherArticleNameChanged.find(({ pickListData: { identifier }, text }) => identifier === element.pickListData.identifier && text === element.text)
        );
      }
    }
    return !!otherArticleNameChanged;
  }
}
